<script setup>
import { computed, ref } from 'vue';
import { Ref } from 'rete-vue-plugin';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  emit: {
    type: Function,
    required: true,
  },
  seed: {
    type: Number,
    default: 0,
  },
});

const showDelete = ref(false);
const lastPrimaryClickAt = ref(0);

const sortByIndex = entries =>
  entries.sort((entryA, entryB) => {
    const indexA = entryA[1]?.index || 0;
    const indexB = entryB[1]?.index || 0;
    return indexA - indexB;
  });

const inputs = computed(() =>
  sortByIndex(Object.entries(props.data.inputs || {}))
);
const nodeType = computed(() => props.data.data?.type || 'text');
const outputs = computed(() =>
  sortByIndex(Object.entries(props.data.outputs || {})).filter(([key]) => {
    return !(
      nodeType.value === 'button' &&
      key === 'next' &&
      props.data.data?.config?.nextStepType === 'flow'
    );
  })
);
const nodeDescription = computed(() => props.data.data?.description || '');
const nodeSummary = computed(
  () => props.data.data?.summary || nodeDescription.value
);
const nodeIcon = computed(
  () => props.data.data?.icon || 'i-lucide-message-square'
);
const deleteLabel = computed(() => props.data.data?.deleteLabel || '');
const cancelLabel = computed(() => props.data.data?.cancelLabel || '');
const nodeStats = [
  { icon: 'i-lucide-send', color: 'text-n-blue-9', value: 0 },
  { icon: 'i-lucide-users', color: 'text-n-teal-9', value: 0 },
  { icon: 'i-lucide-bug', color: 'text-n-ruby-9', value: 0 },
];
const canDelete = computed(() => nodeType.value !== 'start');
const childOutputTypes = {
  buttons: 'button',
  list: 'keyboard',
  sections: 'section',
  rows: 'row',
};

const canCreateChildFromOutput = key =>
  Boolean(childOutputTypes[key]) && Boolean(props.data.outputs?.[key]);

const isMediaHeaderInteractive = computed(
  () =>
    nodeType.value === 'interactive' &&
    props.data.data?.config?.header?.enabled &&
    props.data.data?.config?.header?.type === 'media'
);

const isNodeIncomplete = computed(() => {
  const config = props.data.data?.config || {};

  if (nodeType.value === 'start') return !config?.title?.trim();
  if (nodeType.value === 'noMatch') return false;
  if (nodeType.value === 'text') return !config?.body?.trim();

  if (['image', 'video', 'audio', 'file'].includes(nodeType.value)) {
    return !config?.blobId && !config?.mediaLink?.trim();
  }

  if (nodeType.value === 'locationRequest') return !config?.body?.trim();

  if (nodeType.value === 'location') {
    const latitude = Number(config?.latitude);
    const longitude = Number(config?.longitude);
    return !(
      Number.isFinite(latitude) &&
      Number.isFinite(longitude) &&
      latitude >= -90 &&
      latitude <= 90 &&
      longitude >= -180 &&
      longitude <= 180
    );
  }

  if (nodeType.value === 'contact') {
    return !config?.formattedName?.trim() || !config?.phoneNumber?.trim();
  }

  if (nodeType.value === 'template') {
    return !config?.templateId || !config?.templateName;
  }

  if (nodeType.value === 'interactive') return !config?.body?.trim();

  if (nodeType.value === 'button') {
    if (config?.nextStepType === 'flow') return !config?.linkedFlowId;
    return !config?.title?.trim();
  }

  if (nodeType.value === 'row') {
    return !config?.title?.trim() || !config?.payload?.trim();
  }

  if (nodeType.value === 'carousel') {
    const cards = Array.isArray(config?.cards) ? config.cards : [];
    if (!config?.body?.trim()) return true;
    if (cards.length < 2 || cards.length > 10) return true;

    return cards.some(card => {
      const hasMedia = card?.blobId || card?.mediaLink?.trim();
      if (!hasMedia) return true;

      if (config?.actionType === 'url') {
        return !card?.buttonText?.trim() || !card?.buttonUrl?.trim();
      }

      if (config?.actionType === 'quickReply') {
        const replies = Array.isArray(card?.quickReplies)
          ? card.quickReplies
          : [];
        return (
          !replies.length ||
          replies.length > 2 ||
          replies.some(reply => !reply?.title?.trim())
        );
      }

      return false;
    });
  }

  if (nodeType.value === 'ai') return false;

  return false;
});

const isChildOutputDisabled = key =>
  key === 'list' && isMediaHeaderInteractive.value;

const showDeleteAction = () => {
  if (canDelete.value) showDelete.value = true;
};

const deleteNode = () => {
  showDelete.value = false;
  window.dispatchEvent(
    new CustomEvent('conversation-flow-delete-node', {
      detail: { nodeId: props.data.id },
    })
  );
};

const hideDeleteAction = () => {
  showDelete.value = false;
};

const editNode = () => {
  window.dispatchEvent(
    new CustomEvent('conversation-flow-edit-node', {
      detail: { nodeId: props.data.id },
    })
  );
};

const addChildNode = outputKey => {
  window.dispatchEvent(
    new CustomEvent('conversation-flow-add-child-node', {
      detail: { nodeId: props.data.id, outputKey },
    })
  );
};

const handlePointerUp = event => {
  if (event.button !== 0) return;

  const now = Date.now();
  if (now - lastPrimaryClickAt.value < 350) {
    editNode();
    lastPrimaryClickAt.value = 0;
    return;
  }

  lastPrimaryClickAt.value = now;
};

const nodeClasses = computed(() => {
  const palette = {
    start: 'bg-n-teal-2',
    text: 'bg-n-solid-1',
    interactive: 'bg-n-blue-2',
    media: 'bg-n-amber-2',
    condition: 'bg-n-violet-2',
    action: 'bg-n-ruby-2',
    handoff: 'bg-n-slate-3',
  };

  if (isNodeIncomplete.value) {
    return [
      'border-n-ruby-8 bg-n-ruby-1 shadow-md shadow-n-ruby-4/20',
      props.data.selected ? 'ring-2 ring-n-ruby-5/40' : '',
    ];
  }

  return [
    props.data.selected
      ? 'border-n-blue-9 shadow-lg shadow-n-blue-3/30'
      : 'border-n-slate-5 shadow-sm',
    palette[nodeType.value] || palette.text,
  ];
});
</script>

<template>
  <div
    class="relative w-[260px] overflow-visible border-[1.5px] rounded-lg cursor-pointer select-none text-n-slate-12"
    :class="nodeClasses"
    data-testid="node"
    @contextmenu.prevent.stop="showDeleteAction"
    @dblclick.capture.prevent.stop="editNode"
    @pointerup="handlePointerUp"
  >
    <div
      v-if="showDelete"
      class="absolute z-10 flex items-center gap-2 p-2 border rounded-lg shadow-lg top-3 right-3 bg-n-solid-1 border-n-weak"
      @pointerdown.stop
    >
      <button
        type="button"
        class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium border rounded-md border-n-ruby-5 text-n-ruby-10 bg-n-ruby-2 hover:bg-n-ruby-3"
        @click.stop="deleteNode"
      >
        <span class="i-lucide-trash-2 size-4" />
        <span>{{ deleteLabel }}</span>
      </button>
      <button
        type="button"
        class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium border rounded-md border-n-weak text-n-slate-11 bg-n-alpha-1 hover:bg-n-alpha-2"
        @click.stop="hideDeleteAction"
      >
        <span>{{ cancelLabel }}</span>
      </button>
    </div>

    <div class="flex items-start justify-between gap-3 px-4 py-3">
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <span :class="nodeIcon" class="flex-shrink-0 size-4 text-n-blue-10" />
          <h3 class="m-0 text-sm font-semibold truncate text-n-slate-12">
            {{ data.label }}
          </h3>
        </div>
        <p class="mt-1 mb-0 text-xs leading-4 text-n-slate-11">
          {{ nodeDescription }}
        </p>
      </div>
      <span
        class="px-1.5 py-0.5 text-[10px] rounded-md bg-n-alpha-2 text-n-slate-11 capitalize"
      >
        {{ nodeType }}
      </span>
    </div>

    <div class="grid grid-cols-3 gap-2 px-4 pb-3 text-[10px] text-n-slate-11">
      <div
        v-for="stat in nodeStats"
        :key="stat.icon"
        class="flex flex-col items-center"
      >
        <span :class="[stat.icon, stat.color]" class="size-3" />
        <span>{{ stat.value }}</span>
      </div>
    </div>

    <div
      class="px-4 py-3 border-y border-dashed border-n-slate-4/70 bg-n-alpha-1"
    >
      <p class="m-0 text-xs leading-5 line-clamp-2 text-n-slate-11">
        {{ nodeSummary }}
      </p>
    </div>

    <div class="px-4 py-2">
      <div
        v-for="[key, input] in inputs"
        :key="`input-${key}-${seed}`"
        class="relative flex min-h-8 items-center gap-2 pl-6 text-xs text-n-slate-12"
        :data-testid="`input-${key}`"
      >
        <Ref
          class="absolute z-30 inset-y-0 left-0 flex items-center -translate-x-1/2"
          :emit="emit"
          :data="{
            type: 'socket',
            side: 'input',
            key,
            nodeId: data.id,
            payload: input.socket,
          }"
          data-testid="input-socket"
        />
        <span class="truncate leading-5">{{ input.label }}</span>
      </div>

      <div
        v-for="[key, output] in outputs"
        :key="`output-${key}-${seed}`"
        class="relative flex min-h-8 items-center justify-end gap-2 pr-6 text-xs text-n-slate-12"
        :data-testid="`output-${key}`"
      >
        <button
          v-if="canCreateChildFromOutput(key)"
          type="button"
          class="inline-flex items-center gap-1 px-2 py-1 text-xs border rounded-md border-n-weak text-n-blue-10 bg-n-alpha-1 hover:bg-n-blue-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-n-alpha-1"
          :disabled="isChildOutputDisabled(key)"
          @pointerdown.stop
          @click.stop="addChildNode(key)"
        >
          <span class="i-lucide-plus size-3" />
          <span>{{ output.label }}</span>
        </button>
        <span v-else class="truncate text-right leading-5">
          {{ output.label }}
        </span>
        <Ref
          class="absolute z-30 inset-y-0 right-0 flex items-center translate-x-1/2"
          :emit="emit"
          :data="{
            type: 'socket',
            side: 'output',
            key,
            nodeId: data.id,
            payload: output.socket,
          }"
          data-testid="output-socket"
        />
      </div>
    </div>
  </div>
</template>
